// Improved language detection based on common words and patterns
export const detectLanguage = (text) => {
  if (!text || typeof text !== 'string') {
    return 'en'; // Default to English
  }

  const cleanText = text.toLowerCase().trim();
  
  // French indicators - more specific and unique
  const frenchWords = [
    // Unique French articles
    'le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'd\'', 'l\'',
    // Unique French conjunctions
    'et', 'ou', 'mais', 'donc', 'car', 'ni', 'que', 'qui', 'quoi', 'où', 'quand', 'comment', 'pourquoi',
    // Unique French prepositions
    'avec', 'sans', 'dans', 'sur', 'sous', 'entre', 'parmi', 'vers', 'chez', 'jusqu\'', 'depuis',
    // Unique French verbs
    'est', 'sont', 'était', 'étaient', 'avoir', 'être', 'faire', 'aller', 'venir', 'voir', 'savoir',
    'pouvoir', 'vouloir', 'devoir', 'falloir', 'dire', 'prendre', 'mettre', 'donner', 'parler',
    // Unique French pronouns
    'il', 'elle', 'nous', 'vous', 'ils', 'elles', 'je', 'tu', 'on', 'ce', 'cette', 'ces',
    // French-specific words
    'français', 'française', 'françaises', 'français', 'oui', 'non', 'merci', 'bonjour', 'au revoir',
    's\'il', 'vous', 'plait', 'excusez', 'moi', 'pardon', 'désolé', 'désolée'
  ];
  
  // Spanish indicators - more specific and unique
  const spanishWords = [
    // Unique Spanish articles
    'el', 'la', 'los', 'las', 'un', 'una', 'del', 'al',
    // Unique Spanish conjunctions
    'y', 'o', 'pero', 'por', 'para', 'como', 'porque', 'que', 'quien', 'cual',
    // Unique Spanish prepositions
    'con', 'sin', 'sobre', 'bajo', 'entre', 'durante', 'después', 'antes', 'cuando', 'donde',
    // Unique Spanish verbs
    'es', 'son', 'era', 'eran', 'tener', 'ser', 'estar', 'hacer', 'ir', 'venir', 'ver', 'saber',
    'poder', 'querer', 'deber', 'decir', 'dar', 'hablar', 'tomar', 'poner', 'salir',
    // Unique Spanish pronouns
    'yo', 'tú', 'él', 'ella', 'nosotros', 'vosotros', 'ellos', 'ellas', 'me', 'te', 'se', 'nos',
    // Spanish-specific words
    'español', 'española', 'españoles', 'españolas', 'sí', 'no', 'gracias', 'hola', 'adiós',
    'por', 'favor', 'disculpe', 'perdón', 'lo', 'siento'
  ];
  
  // German indicators - more specific to avoid false positives
  const germanWords = [
    'der', 'die', 'das', 'ein', 'eine', 'und', 'oder', 'aber', 'denn', 'weil', 'dass', 'wenn',
    'wo', 'wie', 'warum', 'was', 'wer', 'welcher', 'ist', 'sind', 'waren', 'haben', 'sein',
    'werden', 'können', 'müssen', 'sollen', 'wollen', 'dürfen', 'mögen', 'ich', 'du', 'er', 'sie',
    'es', 'wir', 'ihr', 'sie', 'deutsch', 'deutsche', 'ja', 'nein', 'danke', 'bitte',
    // More specific German words to avoid false positives
    'nicht', 'auch', 'nur', 'noch', 'schon', 'immer', 'oft', 'manchmal', 'vielleicht',
    'hier', 'dort', 'heute', 'gestern', 'morgen', 'jetzt', 'dann', 'zuerst', 'endlich'
  ];
  
  // Italian indicators
  const italianWords = [
    'il', 'lo', 'la', 'i', 'gli', 'le', 'un', 'una', 'di', 'del', 'della', 'dei', 'delle',
    'in', 'su', 'con', 'per', 'da', 'tra', 'fra', 'e', 'o', 'ma', 'però', 'quando', 'dove',
    'come', 'perché', 'che', 'chi', 'quale', 'è', 'sono', 'era', 'erano', 'avere', 'essere',
    'fare', 'andare', 'venire', 'vedere', 'sapere', 'potere', 'volere', 'dovere', 'io', 'tu',
    'lui', 'lei', 'noi', 'voi', 'loro', 'italiano', 'italiana', 'sì', 'no', 'grazie', 'ciao'
  ];

  // English indicators - more comprehensive
  const englishWords = [
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by',
    'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did',
    'will', 'would', 'could', 'should', 'may', 'might', 'can', 'must', 'shall', 'i', 'you',
    'he', 'she', 'it', 'we', 'they', 'me', 'him', 'her', 'us', 'them', 'my', 'your', 'his',
    'her', 'its', 'our', 'their', 'this', 'that', 'these', 'those', 'what', 'when', 'where',
    'why', 'how', 'who', 'which', 'yes', 'no', 'thank', 'you', 'hello', 'goodbye', 'please',
    'sorry', 'excuse', 'me', 'english', 'american', 'british'
  ];

  // Count exact word matches only (no partial matches)
  const words = cleanText.split(/\s+/);
  const wordCounts = {
    fr: 0,
    es: 0,
    de: 0,
    it: 0,
    en: 0
  };

  // Count French words - exact matches only
  frenchWords.forEach(word => {
    wordCounts.fr += words.filter(w => w === word).length;
  });

  // Count Spanish words - exact matches only
  spanishWords.forEach(word => {
    wordCounts.es += words.filter(w => w === word).length;
  });

  // Count German words - exact matches only
  germanWords.forEach(word => {
    wordCounts.de += words.filter(w => w === word).length;
  });

  // Count Italian words - exact matches only
  italianWords.forEach(word => {
    wordCounts.it += words.filter(w => w === word).length;
  });

  // Count English words - exact matches only
  englishWords.forEach(word => {
    wordCounts.en += words.filter(w => w === word).length;
  });

  // Additional French-specific patterns (accented characters and contractions)
  const frenchPatterns = [
    /[àâäéèêëïîôöùûüÿç]/g, // French accented characters
    /\b(qu\'|d\'|l\'|n\'|s\'|j\'|t\'|m\'|c\')\b/g // French contractions
  ];

  // Additional Spanish-specific patterns (accented characters)
  const spanishPatterns = [
    /[áéíóúñü]/g // Spanish accented characters
  ];

  // Count pattern matches
  frenchPatterns.forEach(pattern => {
    const matches = cleanText.match(pattern);
    if (matches) {
      wordCounts.fr += matches.length;
    }
  });

  spanishPatterns.forEach(pattern => {
    const matches = cleanText.match(pattern);
    if (matches) {
      wordCounts.es += matches.length;
    }
  });

  // Find the language with the highest count
  const detectedLanguage = Object.keys(wordCounts).reduce((a, b) => 
    wordCounts[a] > wordCounts[b] ? a : b
  );

  // Only return detected language if it has a reasonable count
  // Otherwise default to English
  const totalWords = words.length;
  const confidence = wordCounts[detectedLanguage] / totalWords;
  
  // Check for common English academic/historical terms that might trigger false positives
  const englishAcademicTerms = [
    'world', 'war', 'history', 'battle', 'army', 'navy', 'air', 'force', 'military',
    'government', 'politics', 'economy', 'science', 'mathematics', 'biology', 'chemistry',
    'physics', 'literature', 'philosophy', 'art', 'music', 'culture', 'society',
    'revolution', 'independence', 'democracy', 'republic', 'monarchy', 'empire',
    'century', 'decade', 'year', 'month', 'day', 'time', 'period', 'era', 'age'
  ];
  
  // If the text contains English academic terms, boost English confidence
  const englishAcademicMatches = words.filter(word => 
    englishAcademicTerms.includes(word)
  ).length;
  
  const adjustedEnglishCount = wordCounts.en + englishAcademicMatches;
  const adjustedConfidence = adjustedEnglishCount / totalWords;
  
  // Higher threshold for non-English languages to avoid false positives
  if (detectedLanguage === 'en' && adjustedConfidence > 0.05) {
    return 'en';
  } else if (detectedLanguage !== 'en' && confidence > 0.2) { // 20% threshold for other languages
    return detectedLanguage;
  }
  
  return 'en'; // Default to English
};

// Get language-specific instructions for AI
export const getLanguageInstructions = (language) => {
  const instructions = {
    en: 'Create flashcards in English. Use clear, simple English language.',
    fr: 'Create flashcards in French. Use clear, simple French language. Respond in French.',
    es: 'Create flashcards in Spanish. Use clear, simple Spanish language. Respond in Spanish.',
    de: 'Create flashcards in German. Use clear, simple German language. Respond in German.',
    it: 'Create flashcards in Italian. Use clear, simple Italian language. Respond in Italian.'
  };
  
  return instructions[language] || instructions.en;
};